import {quat, v2, v3} from "./short3.js";
import Maths3 from "./Maths3.js";
import Commons from "oc-commons/Commons.js";
import ThreeBSP from "./ThreeBSP";

// statische Hilfsvariablen, die in einigen Methoden benutzt werden
const edge = new THREE.Vector3();
const normal = new THREE.Vector3();
const EPSILON = 0.000001;
const matrix = new THREE.Matrix4();

/**
 * Statische Utility-Klasse zum Abfragen und Manipulieren der Scene und Objektgeometrien
 *
 * Weitere Hilfsroutinen finden sich in three.convenience.js, wo diverese Threejs-Objekte monkeypatched werden
 */
export default class Scenes {

    // ---------------------------------------------------------------------------------
    // Objekte Löschen
    // ---------------------------------------------------------------------------------

    /**
     * Löscht ein Object von seinem Parent, falls Object und Parent existieren
     */
    static remove(object) {
        if (object && object.parent) {
            object.parent.remove(object);
        }
        return object;
    }

    /**
     * Löscht alle Children eines Objectes
     */
    static empty(object) {
        _.clone(object.children).forEach(o=>object.remove(o));
        return object;
    }

    /**
     * Entfernt alle Lichtquellen der Scene
     */
    static removeLights() {
        const lights = Scenes.filter(STAGE.scene,x=>x instanceof THREE.Light);
        lights.forEach(o=>Scenes.remove(o));
    }

    // ---------------------------------------------------------------------------------
    // Traversierung / Filter / Map
    // ---------------------------------------------------------------------------------

    /**
     * Gibt alle Objekte des Baums zurück, für die predicate(object)==true ist (depth-first).
     * @param {boolean} [predicate=()=>true]
     * @returns {Array}
     */
    static filter(object, predicate = ()=>true) {
        const list = [];
        object.traverse((node) => {
            if (predicate(node)) {
                list.push(node);
            }
        });
        return list;
    }

    /**
     * Wie filter, aber im Gegensatz dazu muss auch für die besuchten Parents eines Objektes predicate true sein.
     * @param {boolean} [predicate=(object)=>true]
     * @returns {Array}
     */
    static filterWhile(object, predicate = (object)=>true) {
        const list = [];
        Scenes.traverseWhile(object, predicate, o=>list.push(o));
        return list;
    }

    /**
     * Mappt alle Objekte des Baums
     * @param {*} [mapper=(o)=>o]
     * @returns {Array}
     */
    static map(object, mapper = (o)=>o) {
        const list = [];
        object.traverse((node) => {
            const o = mapper(node);
            if (o) {
                list.push(o);
            }
        });
        return list;
    }


    /**
     * Findet ein Objekt oder einen Punkt anhand eines SelectorStrings. Dieser kann sein:
     *   1. ein Punkt wie: 0,0.5,-1.2
     *   2. der Name eines Objektes wie: Schrank
     *   3. ein zu evaluierender Pfadausdruck wie find("Schrank").nthChild(2).filter("Schublade").get(3).parent().grid(0,0,1).add(0,0,1) oder cameraTarget() oder cameraPosition()
     */
    static findBySelector(object, selectorString) {
        if (/^(?:\s*[-]?\d+(?:\.\d+)?\s*,\s*[-]?\d+(?:\.\d+)?\s*,\s*[-]?\d+(?:\.\d+)?\s*)?$/g.test(selectorString)) {
            // 1. ein Punkt wie 0,0.5,-1.2
            const p = selectorString.split(/\s*,\s*/);
            const o = v3().fromArray(p.map(parseFloat));
            return o;
        } else if (/(parent|filter|find|nthChild)\("/.test(selectorString)) {
            // 3. Ein zu evaluierender Pfadausdruck wie find("Schrank").parent().filter("Schublade")[3].nthChild(1).grid(0,0,1).add(0,0,1)
            const selector = (object) => {
                const wrap = callback => object==null ? ()=>selector(null) : (...args)=>selector(callback(...args));
                return {
                    parent: wrap(()=>object.parent),
                    cameraTarget: wrap(()=>STAGE.orbitControl.target.clone()),
                    cameraPosition: wrap(()=>STAGE.camera.position.clone()),
                    filter: wrap(name => Scenes.filter(object,x=>x.name==name)),
                    find: wrap(name => Scenes.find(object,x=>x.name==name)),
                    get: wrap(n => object[n]),
                    nthChild: wrap(n => object.children[n]),
                    grid: wrap((x,y,z) => {
                        const box = Scenes.boundingBoxLocalMatrix(object);
                        return box.grid(x,y,z);
                    }),
                    add: wrap((x,y,z) => (object instanceof THREE.Vector3 ? object : object.position).clone().add(v3(x,y,z))),
                    object
                }
            };
            const selectorFunction = Function('selector', 'with (selector) { return ' + selectorString + '; }');
            try {
                return selectorFunction(selector(object)).object;
            } catch (e) {
                console.error('Scenes.findBySelector error on ',object,' evaluating ',selectorString,' error is ',e);
                return null;
            }
        } else {
            // 2. der Name eines Objektes
            return Scenes.find(object, x=>x.name==selectorString);
        }
    }

    /**
     * Gibt das erste Objekt des Baums zurück, für das predicate(object)==true ist (depth-first)
     */
    static find(object, predicate) {
        return down(object);
        function down(node) {
            if (!node) {
                return null;
            }
            if (predicate(node)) {
                return node;
            }
            if (node.children) {
                for (let i = 0, l = node.children.length; i < l; i++) {
                    const result = down(node.children[i]);
                    if (result) {
                        return result;
                    }
                }
            }
            return null;
        }
    }

    /**
     * Iteriert im Baum so lange nach oben (parent oder das Objekt selbst), bis predicate(object)==true ist.
     * @returns {?THREE.Object3D}
     */
    static findUp(object, predicateFn) {
        while (object != undefined && object !== null && object !== window && !predicateFn(object)) {
            object = object.parent;
        }
        if (object === window) {
            return null;
        }
        return object;
    }

    /**
     * gibt alle Parents des Objektes einschließlich sich selbst als Set zurück
     */
    static getSelfAndAncestorSet(object) {
        const set = new Set();
        while (object != undefined && object !== null && object !== window) {
            set.add(object);
            object = object.parent;
        }
        return set;
    }

    /**
     * Prüft, ob possibleChild ein direktes oder indirektes Kind von parent ist.
     */
    static isAnchestorOf(parent, possibleChild) {
        let o = possibleChild;
        while (o != null && o !== window) {
            o = o.parent;
            if (o===parent) {
                return true;
            }
        }
        return false;
    }

    /**
     * iteriert über alle Vertices des Objectes und aller Kinder und ruft jeweils iterator(vertex) auf.
     * ACHTUNG! Es handelt sich dabei immer um dieselbe Vertex-Instanz, nur mit anderen Werten! Will man
     * die Vertices speichern, sollte man sie clonen!
     */
    static traverseVertices(object, iterator) {
        const v1 = v3();
        object.updateMatrixWorld(true);
        object.traverse(function (node) {
            if (node instanceof THREE.Sprite) {
                return;
            }
            const geometry = node.geometry;
            if (geometry !== undefined) {
                if (geometry instanceof THREE.Geometry) {
                    const vertices = geometry.vertices;
                    for (let i = 0, il = vertices.length; i < il; i++) {
                        v1.copy(vertices[i]);
                        v1.applyMatrix4(node.matrixWorld);
                        iterator(v1);
                    }
                } else if (geometry instanceof THREE.BufferGeometry && geometry.attributes['position'] !== undefined) {
                    const positions = geometry.attributes['position'].array;
                    for (let i = 0, il = positions.length; i < il; i += 3) {
                        v1.set(positions[i], positions[i + 1], positions[i + 2]);
                        v1.applyMatrix4(node.matrixWorld);
                        iterator(v1);
                    }
                }
            }
        });
    }


    /**
     * iteriert über alle Vertices des Objectes und aller Kinder und ruft jeweils iterator(vertex) auf.
     * ACHTUNG! Ist bei BufferGeometry sehr inneffizient, weil erst eine Geometry daraus erzeugt wird.
     */
    static traverseFaces(object, iterator) {
        const v1 = v3();
        object.updateMatrixWorld(true);
        object.traverse((node) => {
            if (node instanceof THREE.Sprite) {
                return;
            }
            let geometry = node.geometry;
            if (geometry == null) {
                return;
            }
            if (geometry instanceof THREE.BufferGeometry) {
                geometry = new THREE.Geometry().fromBufferGeometry(geometry);
            }
            geometry.faces.forEach((face, index) => iterator(face, index, geometry, node));
        });
    }

    /**
     * wie traverseVertices, aber als Liste aller Vertices (gecloned). Im Zweifel diese Methode nehmen!
     */
    static getVertices(object) {
        const vertices = [];
        Scenes.traverseVertices(object, v=>vertices.push(v.clone()))
        return vertices;
    }

    /**
     * Sucht zu der Position eines Objektes das mit dem nahesten Position
     */
    static findNearestObjectPosition(object, otherObjects) {
        return _.head(_.sortBy(otherObjects, o=>o.position.distanceToSquared(object.position)));
    }

    /**
     * Traversiert den Objektbaum für alle Objekte, für die predicate true ist. Sobald predicate false ergibt,
     * wird das Objekt und alle seine Unterobjekte nicht besucht.
     * callbackDown wird vor dem Abstieg, und callbackUp nach dem Abstieg aufgerufen.
     */
    static traverseWhile(object, predicate = (object)=>true, callbackDown = (object)=> {}, callbackUp = (object)=> {}) {
        if (object && predicate(object)) {
            callbackDown(object);
            if (object.children) {
                for (let i = 0; i < object.children.length; i++) {
                    Scenes.traverseWhile(object.children[i], predicate, callbackDown, callbackUp);
                }
            }
            callbackUp(object);
        }
    }

    /**
     * gibt alle Objekte der Szene zurück, deren Name gleich name ist. Durchsucht den gesamten Scenen-Baum.
     */
    static getObjectsByNameRecursive(name) {
        return Scenes.filter(STAGE.scene, o=>o.name==name);
    }

    /**
     * gibt die gemeinsame Wurzel (Parent) von a und b zurück
     * @param {THREE.Object3D} a
     * @param {THREE.Object3D} b
     * @returns {?THREE.Object3D}
     */
    static getFirstCommonAnchestor(a, b) {
        const otherAncestorSet = Scenes.getSelfAndAncestorSet(b);
        do {
            if (otherAncestorSet.has(a)) {
                return a;
            }
        } while (a = a.parent);
        return null;
    }

    /**
     * gibt den weitesten an der Wurzel liegenden Parent von a zurück,
     * der nicht auch Parent von b ist.
     * @param {THREE.Object3D} a
     * @param {THREE.Object3D} b
     * @returns {?THREE.Object3D}
     */
    static getLastUncommenAnchestor(a, b) {
        const common = Scenes.getFirstCommonAnchestor(a,b);
        if (!common) {
            return null;
        }
        const otherAncestorSet = Scenes.getSelfAndAncestorSet(b);
        while (a.parent && !otherAncestorSet.has(a.parent)) {
            a = a.parent;
        }
        return a;
    }


    // ---------------------------------------------------------------------------------
    // Convenience-Methoden für die Erzeugung von Standardgeometrien
    // ---------------------------------------------------------------------------------

    /**
     * erzeugt ein Box-Mesh
     */
    static box(size = v3(1 * STAGE.mInUnit, 1 * STAGE.mInUnit, 1 * STAGE.mInUnit), material = {color: 0xa0a0a0}) {

        material = material instanceof THREE.Material
            ? material
            : new THREE.MeshPhongMaterial(material);

        const geometry = new THREE.BoxBufferGeometry(size.x, size.y, size.z);
        const mesh = new THREE.Mesh(geometry, material);
        mesh.castShadow = true;
        mesh.receiveShadow = true;
        return mesh;
    }

    /**
     * erzeugt ein Sphere-Mesh
     */
    static sphere(radius = 1, material = {color: 0xa0a0a0}, parts = 16) {

        material = material instanceof THREE.Material
            ? material
            : new THREE.MeshPhongMaterial(material);

        const geometry = new THREE.SphereBufferGeometry(radius, parts, parts);
        const mesh = new THREE.Mesh(geometry, material);
        mesh.castShadow = true;
        mesh.receiveShadow = true;
        return mesh;
    }


    /**
     * erzeugt eine Linie (Array von 2 Punkten oder THREE.Line3)
     */
    static line(pointsOrLine3, material = {color: 0xa0a0a0, linewidth: 1}) {

        material = material instanceof THREE.Material
            ? material
            : new THREE.LineBasicMaterial(material);

        if (pointsOrLine3 instanceof THREE.Line3) {
            pointsOrLine3 = [pointsOrLine3.start, pointsOrLine3.end];
        }

        const line = new THREE.Line(new THREE.BufferGeometry().setFromPoints(pointsOrLine3), material);
        line.computeLineDistances();
        return line;
    }

    /**
     * Erzeugt eine Plane (Eingabe ist THREE.Plane)
     */
    static plane(plane, size = v2(1 * STAGE.mInUnit, 1 * STAGE.mInUnit), material = {
        color: 0xa0a0a0,
        side: THREE.DoubleSide
    }, widthSegments = 1, heightSegments = 1) {

        material = material instanceof THREE.Material
            ? material
            : new THREE.MeshPhongMaterial(material);

        const geometry = new THREE.PlaneBufferGeometry(size.x, size.y, widthSegments, heightSegments);

        const mesh = new THREE.Mesh(geometry, material);
        mesh.quaternion.copy(new THREE.Quaternion().setFromUnitVectors(Maths3.vector.z, plane.normal));
        mesh.position.copy(plane.coplanarPoint(mesh.position));
        return mesh;
    }

    /**
     * Erzeugt einen Cylinder (Eingabe ist oc-threejs/math/Cylinder.js)
     */
    static cylinder(cylinder, material = {color: 0xa0a0a0}) {

        material = material instanceof THREE.Material
            ? material
            : new THREE.MeshPhongMaterial(material);

        const cylinderFinite = isFinite(cylinder.height) ? cylinder : cylinder.clone().setHeight(100);
        const geometry = new THREE.CylinderGeometry(cylinderFinite.radius, cylinderFinite.radius, cylinderFinite.height, 20);

        const mesh = new THREE.Mesh(geometry, material);
        mesh.quaternion.copy(new THREE.Quaternion().setFromUnitVectors(Maths3.vector.y, cylinderFinite.direction));
        mesh.position.copy(cylinderFinite.center());
        return mesh;
    }

    /**
     * Erzeugt einen Cone (Eingabe ist oc-threejs/math/Cone.js)
     */
    static cone(cone, material = {color: 0xa0a0a0}) {

        material = material instanceof THREE.Material
            ? material
            : new THREE.MeshPhongMaterial(material)

        const coneFinite = isFinite(cone.height) ? cone : cone.clone().setHeight(100);
        const geometry = new THREE.CylinderGeometry(coneFinite.radiusBottom(), 0, coneFinite.height, 20);

        const mesh = new THREE.Mesh(geometry, material);
        mesh.quaternion.copy(new THREE.Quaternion().setFromUnitVectors(Maths3.vector.y, coneFinite.direction));
        mesh.position.copy(coneFinite.center());
        return mesh;
    }

    /**
     * erzeugt ein Wireframe zu einem Mesh
     */
    static wireframe(mesh, linewidth = 1, color = 0x909090) {
        const geometry = new THREE.EdgesGeometry( mesh.geometry ); // or WireframeGeometry
        const material = new THREE.LineBasicMaterial( { color, linewidth } );
        const edges = new THREE.LineSegments( geometry, material );
        return edges;
    }

    /**
     * erzeugt einen einzelnen, kleinen Punkt, der aber ein Mesh ist
     */
    static createPointObject() {
        const line = new THREE.Line(new THREE.BufferGeometry().setFromPoints([v3( 0, 0, 0 )]), new THREE.LineBasicMaterial());
        return line;
    }

    // ---------------------------------------------------------------------------------
    // Größen und Position von Objekten ermitteln
    // ---------------------------------------------------------------------------------

    /**
     * Convenience Methode zur besseren lesbarkeit des Codes, welche intern boundingBox(..,..,false) aufruft
     * @param {(THREE.Object3D | Array<THREE.Object3D>)} object oder array<Object3D>
     * @param {boolean} [predicate=(o) => true] optional, eine Funktion (object)=>boolean, die entscheidet, ob das Objekt und seine Kinder
     *        zur BoundingBox dazugezählt werden sollen oder nicht
     * @return {THREE.Box3} Box
     * @deprecated besser boundingBoxLocalMatrix verwenden, weil diese Methode bei Unterobjekten die jeweilige Matrix
     *     ignoriert!
     */
    static boundingBoxLocal(object, predicate = (o) => true) {
        return Scenes.boundingBox(object,predicate,false);
    }

    /**
     * gibt die BoundingBox zu einem Object zurück.
     * ACHTUNG! der Parameter inWorldSpace ist deprecaded und sollte nicht auf false stehen, weil dann bei
     * Unterobjekten die jeweilige Matrix ignoriert wird! Besser dann boundingBoxLocalMatrix verwenden!
     *
     * @param {(THREE.Object3D | Array<THREE.Object3D>)} object oder array<Object3D>
     * @param {boolean} [predicate=(o) => true] optional, eine Funktion (object)=>boolean, die entscheidet, ob das Objekt und seine Kinder
     *        zur BoundingBox dazugezählt werden sollen oder nicht
     * @param {boolean} [inWorldSpace=true] wenn false, dann wird die BoundingBox im lokalen Koordinatensystem des Objektes gebildet
     * @return Box
     */
    static boundingBox(object, predicate = (o) => true, inWorldSpace = true) {
        if (!inWorldSpace) {
            console.warn('Scenes.boundingBoxLocal ist deprecated! Besser boundingBoxLocalMatrix verwenden, weil damit die Matrix von Unterobjekten korrekt berücksichtigt wird!');
        }

        if (_.isArray(object)) {
            return object.map(o=>Scenes.boundingBox(o, predicate, inWorldSpace)).reduce((a,b)=>a.union(b),new THREE.Box3());
        }
        // Code von THREE.Box3().setFromObject kopiert und erweitert, so dass nur solche Objekte
        // zur BoundingBox zählen, bei denen predicate true ergibt
        const box = new THREE.Box3();
        const v1 = new THREE.Vector3();
        if (inWorldSpace) {
            object.updateMatrixWorld(true);
        }
        box.makeEmpty();
        Scenes.traverseWhile(object, predicate, (node)=> {
            const geometry = node.geometry;
            if (geometry !== undefined) {
                if (geometry instanceof THREE.Geometry) {
                    const vertices = geometry.vertices;
                    for (let i = 0, il = vertices.length; i < il; i++) {
                        v1.copy(vertices[i]);
                        if (inWorldSpace) {
                            v1.applyMatrix4(node.matrixWorld);
                        }
                        box.expandByPoint(v1);
                    }
                } else if (geometry instanceof THREE.BufferGeometry) {
                    const attribute = geometry.attributes.position;
                    if ( attribute !== undefined ) {
                        for (let i = 0, l = attribute.count; i < l; i ++ ) {
                            v1.fromBufferAttribute( attribute, i );
                            if (inWorldSpace) {
                                v1.applyMatrix4( node.matrixWorld );
                            }
                            box.expandByPoint( v1 );
                        }
                    }
                }
            }
        });
        return box;
    }

    /**
     * gibt die BoundingBox zu einem Object zurück.
     *
     * @param {THREE.Object3D} object oder array<Object3D>
     * @param {boolean} [predicate=(o) => true] optional, eine Funktion (object)=>boolean, die entscheidet, ob das Objekt und seine Kinder
     *        zur BoundingBox dazugezählt werden sollen oder nicht
     * @param {*} [parentMatrix=new THREE.Matrix4()] optional eine Parent-Matrix
     * @param {boolean} [ignoreOwnMatrix=false] wenn true, dann wird die Matrix des object selbst ignoriert, aber die der Kindobjekte
     *     nicht. Bei false wird die Matrix des Objektes relativ zum Parent berücksichtigt
     * @return Box
     */
    static boundingBoxLocalMatrix(object, predicate = (o) => true, parentMatrix = new THREE.Matrix4(), ignoreOwnMatrix = false) {
        // Code von THREE.Box3().setFromObject kopiert und erweitert, so dass nur solche Objekte
        // zur BoundingBox zählen, bei denen predicate true ergibt
        const box = new THREE.Box3();
        const v1 = new THREE.Vector3();
        box.makeEmpty();

        const traverse = (parentMatrix,object,useMatrix) => {
            if (!predicate(object)) {
                return;
            }
            object.updateMatrix();
            const matrix = useMatrix ? parentMatrix.clone().multiply(object.matrix) : parentMatrix;
            const geometry = object.geometry;
            if (geometry !== undefined) {
                if (geometry instanceof THREE.Geometry) {
                    const vertices = geometry.vertices;
                    for (let i = 0, il = vertices.length; i < il; i++) {
                        v1.copy(vertices[i]);
                        v1.applyMatrix4(matrix);
                        box.expandByPoint(v1);
                    }
                } else if (geometry instanceof THREE.BufferGeometry) {
                    const attribute = geometry.attributes.position;
                    if ( attribute !== undefined ) {
                        for (let i = 0, l = attribute.count; i < l; i ++ ) {
                            v1.fromBufferAttribute( attribute, i );
                            v1.applyMatrix4( matrix );
                            box.expandByPoint( v1 );
                        }
                    }
                }
            }
            object.children.forEach(child=>traverse(matrix,child,true));
        };

        traverse(parentMatrix,object,!ignoreOwnMatrix);

        return box;
    }

    /**
     * gibt die Größe der BoundingBox eines Objectes zurück
     */
    static size(object) {
        return Scenes.boundingBox(object).getSize();
    }

    /**
     * gibt die BoundingSphere zurück
     */
    static boundingSphere(objectsOrSphere) {
        return Scenes.boundingBox(objectsOrSphere).getBoundingSphere();
    }

    /**
     * gibt die gemeinsame BoundingBox für ein array von Objecten zurück
     */
    static unionBounds(objects) {
        const result = Scenes.boundingBox(objects[0]);
        for (let i = 1; i < objects.length; i++) {
            result.union(Scenes.boundingBox(objects[i]));
        }
        return result;
    }

    /**
     * prüft, ob ein Object3D leer ist, also (selbst oder seine Kinder) keine Geometry enthält
     */
    static isEmpty(object) {
        return Scenes.find(object,o=>o.geometry!=null)==null;
    }

    // ---------------------------------------------------------------------------------
    // Positionierung und Orientierung
    // ---------------------------------------------------------------------------------

    /**
     * zentriert ein Objekt im Nullpunkt (anhand seiner BoundingBox)
     */
    static center(object) {
        object.position.set(0, 0, 0);
        const b = Scenes.boundingBox(object);
        Maths3.addPosition(object,
            -(b.max.x + b.min.x) / 2,
            -(b.max.y + b.min.y) / 2,
            -(b.max.z + b.min.z) / 2
        );
        return object;
    }

    /**
     * setzt ein Objekt mit der Unterkante seiner BoundingBox genau auf den Boden (die y-Ebene durch den Nullpunkt)
     */
    static onGround(object) {
        const b = Scenes.boundingBox(object);
        Maths3.addPosition(object,
            0,
            -b.min.y,
            0
        );
        return object;
    }

    /**
     * gibt die "Blickrichtung" eines Objektes zurück (z-Vector seines lokalen Koordinatensystems)
     */
    static getOrientationVector(object) {
        matrix.extractRotation(object.matrixWorld);
        return v3(0, 0, 1).applyMatrix4(matrix);
    }


    /**
     * Orientiert ein Objekt so, dass es in die Kamerarichtung schaut
     * @param options.plane: wenn ungleich null, wird das Objekt wird parallel zur Plane ausgerichtet (THRE.PLane),
     *     sonst frei im Raum
     * @param options.halfSphere: wenn true, wird das Object nur auf 180° genau orientiert. Es wird dann zwar immer
     * mit der Kamera ausgerichtet, aber die Vorder- und Rückseite bleiben möglichst so orientiert wie zuvor.
     * Es dreht sich dann also maximal mit einem Winkel kleiner +-90°.
     */
    static orientateInCameraDirection(object,options={}) {
        const plane = options.plane || null;
        const halfSphere = options.halfSphere || false;

        const camDir = STAGE.camera.getWorldDirection(v3());
        const targetDir = plane ? options.plane.projectPoint(camDir, v3()).normalize() : camDir;

        const q = quat().setFromUnitVectors(Maths3.vector.z, targetDir);

        if (halfSphere) {
            const objectDirection = Scenes.getOrientationVector(object);
            const sign = Math.sign(targetDir.dot(objectDirection)) < 0 ? -1 : 1;
            if (sign<0) {
                q.multiply(Maths3.quaternion.y180);
            }
        }

        object.quaternion.copy(q);
    }

    /**
     * Definiert einen Pivot-Punkt, also den zentralen Drehpunkt eines Objektes.
     * Der Pivot-Punkt wird relativ zum Nullpunkt des übergebenen Objektes angegeben.
     * Der Pivot-Punkt wird als Objekt3D erzeugt und zurückgegeben.
     * Das übergegebene Objekt wird als Child zum Pivot-Objekt hinzugefügt.
     * @param {THREE.Object3D} object
     * @param {THREE.Vector3} point
     * @returns {THREE.Object3D} Pivot Objekt
     */
    static pivot(object, point) {
        const pivot = new THREE.Object3D();
        pivot.add(object);
        pivot.position.add(point);
        object.position.sub(point);
        return pivot;
    }


    /**
     * Rotiert oder verschiebt ein Objekt, so dass eins seiner Kindobjekte eine bestimmte Position oder Ausrichtung
     * relativ zu einem anderen Objekt einnimmt.
     *
     * Diese Methode mit dem unglaublich guten Namen *bring* (ich habe zur Namensfindung einen Thesaurus bemüht)
     * ist die ultimative Schweizer Offizierskettensäge der Objektpositionierung.
     *
     * Die Methode wird mit einem Parameter-Objekt folgenden Aufbaus aufgerufen:
     *
     * <pre><code>
     * {
     *  what: Array of THREE.Vector3|THREE.Quaternion|THREE.Matrix4|THREE.Object3D|Virtual3D,
     *  to: Array of THREE.Vector3|THREE.Quaternion|THREE.Matrix4|THREE.Object3D|Virtual3D,
     *  rotate: boolean,
     *  translate: boolean,
     *  slerpFactor: number,
     * }
     * </code></pre>
     *
     * what wird dabei verschoben/orientiert, so dass es auf to gebracht wird. Diese beiden Arrays werden weiter unten
     * genauer beschrieben.
     *
     * Mit translate und rotate wird angegeben, ob man Positioneren und/oder Orientieren möchte, default ist false.
     *
     * Mit slerpFactor wird optional ein linearer Interpolationsfaktor zwischen 0 und 1 angeben, default ist 1.
     *
     * Es gibt drei verschiedene Anwendungsfälle:
     * 1. Positionierung (translate=true): Hier können im what- und to-Array Objekt, Matrix und Vector stehen.
     *    Vector3 wird als Ortsvektor verstanden.
     *
     * 2. Vollständig definierte Orientierung (rotate=true): Hierbei sind alle drei Achsen der Orientierung durch
     *    das to-Array bestimmt. Im what- und to-Array können Object, Matrix und Quaternion stehen.
     *
     * 3. Ausrichtung an einer Achse (rotate=true): Hierbei ist nur eine Achse der Orientierung durch das to-Array
     *    bestimmt, und die anderen beiden Achsen bleiben möglichst unverändert.
     *    Hierzu muss das letzte Element des what- und to-Arrays jeweils ein Vector sein,
     *    welcher die Richtung der Achse definiert und ein Einheitsvektor ist.
     *
     * what und to sind Arrays, in denen gemischt Objekte, Matrizen, Quaternionen und Vektoren stehen dürfen
     * (statt Object3D können stets auch Virtual3D benutzt werden).
     * Es sind zusammenhängende Ketten von Parent-Child-Beziehungen, also [Parent,Child,Grandchild,...].
     * Das what-Array muss immer mit einem Objekt beginnen - nämlich mit dem Objekt, das tatsächlich gedreht und/oder
     * verschoben werden soll.
     * Statt Objekten reicht es auch, die Matrix4 eines Objektes zu übergeben. Wenn man nur Orientieren
     * und nicht Positionieren möchte, reichen auch Quaternionen, und für Positionierung auch Ortsvektoren.
     * Alle Teile (Matritzen, Quaternionen, Vektoren) werden dabei immer lokal zum jeweiligen Parent verstanden.
     * Nur beim to-Array kann das erste Element eine Matrix4, Quanternion oder Vektor sein -
     * in diesem Fall wird das erste Element im Welt-Koordinatensystem verstanden.
     *
     * @example
     * // Hier wird ein Connector auf dem Connector eines anderen Objektes plaziert, also sowohl positioniert
     * // als auch so ausgerichtet, so dass die beiden Connectoren um genau 180° gedreht aufeinandersitzen.
     * // Die Orientierung in allen Achsen ist dabei durch otherConnector vorgegeben.
     * // connectorParent, also immer das erste Element des what-Arrays, wird bewegt.
     * Scenes.bring({
     *      what: [connectorParent,connector],
     *      to: [otherConnector,Maths3.quaternion.y180],
     *      rotate: true,
     *      translate: true,
     *  });
     *
     * // Hier wird das Objekt "dragged" so verschoben, dass der Nullpunkt der "anchorMatrix"
     * // (die hier dem lokalen Koordinatenssystem eines Kindobjektes von dragged entspricht)
     * // auf dem Vektor (1,2,3) im Weltkoordinatensystem sitzt.
     * Scenes.bring({
     *      what: [dragged,anchorMatrix],
     *      to: [v3(1,2,3)],
     *      translate: true,
     *  });
     *
     * // Hier wird das Objekt "dragged" so orientiert, dass die Y-Achse seines Anchor-Kindobjektes an der Normalen
     * // einer Ebene ausgerichtet wird, also sozusagend das Objekt aufrecht auf die Ebene gestellt wird.
     * Scenes.bring({
     *      what: [dragged,anchor,v3(0,1,0)],
     *      to: [plane.normal],
     *      rotate: true,
     *  });
     */
    static bring({what, to, rotate = false, translate = false, slerpFactor = 1}) {

        const check = (array,name) => {
            if (array.length==0) {
                throw `'${name}' may not be empty`;
            }
            array.forEach(o=>{
                if (o==null) {
                    throw `'${name}' may not contain null values`;
                }
                if (!(o instanceof THREE.Vector3
                        || o instanceof THREE.Quaternion
                        || o instanceof THREE.Matrix4
                        || o.matrix!==undefined
                        || o.matrixWorld!==undefined)) {
                    throw `'${name}' must only contain Vector3, Quaternion, Matrix4 or an Object with a matrix`;
                }
            });
        }

        const multiply = matrixes => matrixes.reduce((acc,cur)=>acc.multiply(cur),new THREE.Matrix4());

        const matrix = a => {
            if (a.matrix) {
                return a.matrix;
            } else if (a instanceof THREE.Matrix4) {
                return a;
            } else if (a instanceof THREE.Quaternion) {
                return new THREE.Matrix4().makeRotationFromQuaternion(a);
            } else if (a instanceof THREE.Vector3) {
                return new THREE.Matrix4().setPosition(a);
            } else {
                throw 'unknown matrix transformation for '+a;
            }
        }

        const getWorldMatrixOfArray = (array) => {
            let isFirst = true;
            let m = new THREE.Matrix4();
            for (let a of array) {
                if (isFirst && a.matrixWorld) {
                    m.multiply(a.matrixWorld);
                } else {
                    m.multiply(matrix(a));
                }
                isFirst = false;
            }
            return m;
        }

        const position = (matrix) => v3().setFromMatrixPosition(matrix);
        const quaternion = (matrix) => matrix.getRotation();
        const inverse = (matrix) => new THREE.Matrix4().copy(matrix).invert();

        check(what,'what');
        check(to,'to');

        const oWhatFirst = _.first(what);
        if (oWhatFirst.matrix==undefined && oWhatFirst.matrixWorld==undefined) {
            throw `the first element of 'what' must be an Object with a matrix`;
        }

        const updateMatrixWorld = (o)=>{
            if (o.updateWorldMatrix) {
                o.updateWorldMatrix(true, true);
            }
        }


        updateMatrixWorld(oWhatFirst);

        if (rotate) {
            const whatLast = _.last(what);
            const toLast = _.last(to);
            const whatLastIsVector = whatLast instanceof THREE.Vector3;
            const toLastIsVector = toLast instanceof THREE.Vector3;

            if (whatLastIsVector || toLastIsVector) {
                if (whatLastIsVector!=toLastIsVector) {
                    throw 'a vector must be aligned to another vector';
                }
                // die beiden letzten Elemente der what und to Arrays sind Richtungsvektoren und definieren
                // jeweils eine Achse. Diese beiden Achsen sollen im Welt-Koordinatensystem parallel zueinander ausgerichtet sein.
                // Die Rotationskomponente von what um diese Achse herum wird nicht verändert.
                what = what.slice(0,-1);
                to = to.slice(0,-1);
                const wmWhat = getWorldMatrixOfArray(what);
                const wmTo = getWorldMatrixOfArray(to);
                const wmiWhatFirst = inverse(oWhatFirst.matrixWorld);
                const wpsWhat = [v3(),whatLast.clone()].map(v=>v.applyMatrix4(wmWhat).applyMatrix4(wmiWhatFirst));
                const wpsTo = [v3(),toLast.clone()].map(v=>v.applyMatrix4(wmTo).applyMatrix4(wmiWhatFirst));
                const luWhat = wpsWhat[1].sub(wpsWhat[0]).normalize();
                const luTo = wpsTo[1].sub(wpsTo[0]).normalize();
                const lqDelta = new THREE.Quaternion().setFromUnitVectors(luWhat,luTo);
                const lqDeltaSlerped = new THREE.Quaternion().slerp ( lqDelta, slerpFactor );
                oWhatFirst.quaternion.multiply(lqDeltaSlerped);
                updateMatrixWorld(oWhatFirst);

            } else {
                // die World-Quaternion von what und to sollen gleich sein (d.h. die Orientierung soll in allen Achsen gleich sein)
                // seit 9.4.18 geht's auch für den Fall, dass der Parent relativ zur Szene gedreht ist

                const backMatrix = multiply(what.map(matrix).slice(1).reverse().map(inverse));
                const wqTo = quaternion(getWorldMatrixOfArray(to).multiply(backMatrix));
                let lqTo = null;
                if (oWhatFirst.parent) {
                    const wqParent = quaternion(oWhatFirst.parent.matrixWorld);
                    lqTo = wqParent.getRotationFromMeTo(wqTo);
                } else {
                    lqTo = wqTo;
                }
                oWhatFirst.quaternion.slerp(lqTo,slerpFactor);
                updateMatrixWorld(oWhatFirst);
            }
        }

        if (translate) {
            // die World-Position von what und to soll gleich sein
            // Matrix: A*B=C => B=I(A)*C
            // I(A*B)=I(B)*I(A)
            const wpWhat = position(getWorldMatrixOfArray(what));
            const wpTo = position(getWorldMatrixOfArray(to));
            const wpDelta = wpTo.sub(wpWhat).multiplyScalar(slerpFactor);
            if (oWhatFirst?.parent?.matrixWorld && !rotate) {
                const wqiParent = quaternion(oWhatFirst.parent.matrixWorld).invert();
                wpDelta.applyQuaternion(wqiParent);
            }
            
            if (oWhatFirst?.parent?.matrixWorld) {
                const worldScaleParent = v3().setFromMatrixScale(oWhatFirst.parent.matrixWorld);
                wpDelta.divide(worldScaleParent);
            }
            oWhatFirst.position.add(wpDelta);
            updateMatrixWorld(oWhatFirst);
        }
    }

    /* ---------------------------------------------------------------------------------
    /* CSG-Operationen (Boolsche Operationen auf Meshs)
    /* ---------------------------------------------------------------------------------
     *  Allgemeine Hinweise:
     *  1. ThreeBSP cached die bsp-Daten am Objekt. Sollte die Geometry eines Objekts upgedated werden,
     *      so müssen also die bsp-Daten gelöscht werden (delete jaco3d.bsp)
     *  2. Die Operationen können erst am Ende von jaco3d.update3d ausgeführt werden (nach der Erstellung der Geometry)
     */

    /**
     * Vereint die Meshs der beiden Object3D a und b und übernimmt das Ergebnis in die Geometry von a
     * Achtung: Geht nur für solide Meshs, also solche ohne Löcher, d.h. wenn man das "Innen" mit Wasser füllt, darf es nicht rauslaufen!
     * @param {THREE.Object3D} a
     * @param {THREE.Object3D} b
     */
    static union(a, b) {
        Scenes._boolean('union',a,b);
    }

    /**
     * Subtrahiert vom Mesh des Object3D a das Mesh des Object3D b und übernimmt das Ergebnis in die Geometry von a
     * ACHTUNG: klappt nicht gut mit Geometrien mit mehreren Materialien. Die MaterialIndex in b werden alle als 0 angenommen!
     * Achtung: Geht nur für solide Meshs, also solche ohne Löcher, d.h. wenn man das "Innen" mit Wasser füllt, darf es nicht rauslaufen!
     * @param {THREE.Object3D} a
     * @param {THREE.Object3D} b
     */
    static subtract(a, b) {
        Scenes._boolean('subtract',a,b);
    }

    /**
     * Bildet die Schnittmenge der beiden Object3D a und b und übernimmt das Ergebnis in die Geometry von a
     * ACHTUNG: klappt nicht gut mit Geometrien mit mehreren Materialien. Die MaterialIndex in b werden alle als 0 angenommen!
     * Achtung: Geht nur für solide Meshs, also solche ohne Löcher, d.h. wenn man das "Innen" mit Wasser füllt, darf es nicht rauslaufen!
     * @param {THREE.Object3D} a
     * @param {THREE.Object3D} b
     */
    static intersect(a, b) {
        Scenes._boolean('intersect',a,b);
    }

    /**
     * @private
     * @param {string} operation
     * @param {THREE.Object3D} a
     * @param {THREE.Object3D} b
     */
    static _boolean(operation, a, b) {
        if (STAGE.settings.supress_boolean_operations_for_debugging) {
            // console.log('Scenes.'+operation+' will not be done on ',a,b,' because STAGE.settings.supress_boolean_operations_for_debugging==true');
            return;
        }
        const t0 = performance.now();

        const anchestor = Scenes.getFirstCommonAnchestor(a,b);
        let aMatrix = null;
        let bMatrix = null;
        if (anchestor) {
            const mulMat = (x,matrix)=>{
                if (x && x!==anchestor) {
                    mulMat(x.parent,matrix);
                    x.updateMatrix();
                    matrix.multiply(x.matrix);
                }
                return matrix;
            };
            aMatrix = mulMat(a.parent,new THREE.Matrix4());
            bMatrix = mulMat(b.parent,new THREE.Matrix4());
            a.updateMatrix();
            b.updateMatrix();
        } else {
            a.updateMatrixWorld(true);
            b.updateMatrixWorld(true);
            aMatrix = null;
            bMatrix = null;
        }

        const absps = ThreeBSP.getBSPs(a,[],aMatrix);
        const bbsps = ThreeBSP.getBSPs(b,[],bMatrix);

        absps.forEach(absp=>{
            const mesh = absp.mesh;
            bbsps.forEach(bbsp=>{
                if (operation!=='union' && STAGE.settings.support_csg_multi_materials===false) {
                    // ACHTUNG! viele Standardgeometrien von THREE wie BoxGeometry haben mehrere MaterialIndizes.
                    // für intersect oder subtract ist das aber nicht gut, weil dabei Polygone von b
                    // nach a übernommen werden können, und damit auch unpassende MaterialIndizes.
                    // Generell können Operationen bei mehreren Masterialien pro Geometrie zu Polygonen
                    // mit falschem Materialindex führen, wenn diese aus b stammen. Daher:
                    bbsp.setMaterialIndexOnAllPolygons(0);
                }

                absp = absp[operation](bbsp);
            });
            absp.setGeometryInto(mesh);
        });

        const dt = performance.now()-t0;
        if (dt>300) {
            console.warn('csg operation '+operation+' takes very long, '+dt.toFixed(0)+'ms, try to simplify the meshes',a,b);
        }
    }

    // ---------------------------------------------------------------------------------
    // Objekte gruppieren (zu einem Objekt zusammenfügen und nachher wieder auflösen)
    // ---------------------------------------------------------------------------------

    /**
     * fügt child als neues Kind zu parent hinzu, ohne die Weltposition von Child zu ändern,
     * und merkt sich, dass das mit dieser Methode erfolgt ist.
     */
    static groupAdd(parent, child) {
        const matrixWorldInverse = new THREE.Matrix4();
        matrixWorldInverse.copy(parent.matrixWorld).invert();
        child.applyMatrix4(matrixWorldInverse);
        parent.add(child);
        child._GROUPED_ = true;
    }

    /**
     * fügt alle Objekte einer Liste als neue Kinder zu parent hinzu, ohne die Weltposition der Kinder zu ändern,
     * und merkt sich, dass das mit dieser Methode erfolgt ist.
     */
    static groupObjects(object, list) {
        //scene.updateMatrixWorld();
        list.forEach(o=> {
            object.attach( o );
            o._GROUPED_ = true;
        });
    }

    /**
     * Entfernt alle mit groupAdd/groupObjects zu einem Parent hinzugefügten childs,
     * ohne die Weltposition der Kinder zu ändern, und fügt sie Top-Level der Szene hinzu.
     */
    static unGroupObjects(object) {
        const scene = STAGE.scene;
        if (object.parent) {
            scene.updateMatrixWorld();
            const grouped = object.children.filter(o=>o._GROUPED_);
            grouped.forEach(o=> {
                delete o._GROUPED_;
                object.parent(o);
            });
        }
    }

    // ---------------------------------------------------------------------------------
    // Sonstiges
    // ---------------------------------------------------------------------------------


    /**
     * Gibt true zurück, wenn das Objekt mit der Stage verbunden ist (Gegenteil von isOrphan, aber wer versteht schon
     * Orphan?)
     */
    static isInScene(object) {
        return !Scenes.isOrphan(object);
    }

    /**
     * Gibt true zurück, wenn das Objekt nicht mit der Stage verbunden ist.
     */
    static isOrphan(object) {
        if (object == null) {
            return false;
        }
        do {
            object = object.parent;
            if (object == null) {
                return true;
            }
            if (object instanceof THREE.Scene) {
                return false;
            }
        } while (true);
    }

    /**
     * Gibt eine Funktion zurück, die maximal einmal pro Rendering aufgerufen wird.
     * @param resultFn Callback, welche das Ergebnis erzeugt
     * @param objectFn Callback, welche das Objekt zurückgibt, auf das sich "nur einmal pro Rendering" bezieht
     * @example
     * // cached die Erzeugung einer BoundingBox, so dass sie maximal einmal pro Frame aufgerufen wird
     * const box = Scenes.cachePerFrame(()=>Scenes.boundingBox(object),()=>object);
     * // erster Aufruf berechnet die Box
     * box().min.x
     * // zweiter Aufruf gibt die gecachte Box zurück
     *
     */
    static cachePerFrame(resultFn, objectFn) {
        let result = null;
        let heartbeat = Number.NaN;
        return () => {
            const object = objectFn();
            if (heartbeat != object.__HEARTBEAT__) {
                heartbeat = object.__HEARTBEAT__;
                result = resultFn(object);
            }
            return result;
        }
    }

    /**
     * Iteriert Punkte in einem Raster in einem Kreis um einen Strahl. Die Punkteliegen in einem
     * Kreis mit radius um den Origin und in der Ebene orthogonal zum Ray. Es werden numberOfRays Punkte iteriert.
     * @param ray originaler Strahl
     * @param radius Umkreis, in dem die Strahlen maximal liegen
     * @param numberOfPoints Anzahl der Punkte. Sie sind gleichmäßig gerastert und füllen den Kreis aus
     * @param iteratee Callback(rasterPoint)
     */
    static iteratePointsOnCircleRaster(ray, radius, numberOfPoints, iteratee) {

        const basis = [v3(), v3()];
        Maths3.generateComplementBasis(ray.direction, basis[0], basis[1]);

        const rasterPointsOnSquareSide = Math.max(1, Math.ceil(Math.sqrt(4 / Math.PI * numberOfPoints)));
        const rasterLength = 2 * radius / rasterPointsOnSquareSide;

        const v = (v, scale) => v.clone().multiplyScalar(scale * rasterLength - radius);

        //const rasterRay = ray.clone();
        const origin = ray.origin.clone();

        for (let i = 0; i < rasterPointsOnSquareSide; i++) {
            for (let j = 0; j < rasterPointsOnSquareSide; j++) {
                const rasterPoint = v(basis[0], i).add(v(basis[1], j)).add(origin);
                //Utils.debugPoint(rasterPoint);
                if (rasterPoint.distanceTo(origin) > radius) {
                    continue;
                }
                if (iteratee(rasterPoint)) {
                    return;
                }
            }
        }
    }

    /**
     * iteriert auf den Seiten eines regelmäßigen, konvexen 2D-Polygons (wie ein Quadrat) und
     * ruft jeweils ein Callback für jeden Punkt auf
     * @param options {
     *      center: Maths3.vector.zero, // Mittelpunkt des Polygons
     *      sideZeroNormal: Maths3.vector.z, // Wohin zeigt die Seite 0 des Polygons
     *      planeNormal: Maths3.vector.y, // definiert die Ebene, hier parallel zum Boden
     *      radius: 1, // Radius des Polygons
     *      numberOfSides: 4, // Anzahl der Seiten
     *      positionsPerSide: [0], // gibt an, welche Punkte pro Seite an Iteratee übergeben werden sollen. 0 ist die
     *     Mitte, -1 ist eine Einheit links usw.
     * }
     * @param iteratee Aufruf iteratee({point, middle, edges, sideNormal, sideIndex, positionValue, options})
     */
    static iteratePointsOnSideOfRegularConvexPolygon(options, iteratee) {
        options = Commons.defaultsDeepSafe({}, options, {
            center: Maths3.vector.zero,
            sideZeroNormal: Maths3.vector.z,
            planeNormal: Maths3.vector.y,
            radius: 1,
            numberOfSides: 4,
            positionsPerSide: ["0%"],
        });
        if (!options.filterSideIndizes) {
            options.filterSideIndizes = _.range(options.numberOfSides);
        }
        const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(options.planeNormal, options.center);
        const sideZeroCoplanarNormal = plane.projectPoint(options.center.clone().add(options.sideZeroNormal),v3()).sub(options.center).normalize();
        const sideAngle = 2 * Math.PI / options.numberOfSides;
        options.filterSideIndizes.forEach(sideIndex=> {
            const edges = [-sideAngle / 2, +sideAngle / 2].map(a=>sideZeroCoplanarNormal.clone().multiplyScalar(options.radius).applyAxisAngle(options.planeNormal, a + sideAngle * sideIndex).add(options.center));
            const middle = edges[0].clone().add(edges[1]).multiplyScalar(0.5);
            const halfLine = edges[1].clone().sub(middle);
            const halfLineLength = halfLine.length();
            const sideNormal = middle.clone().sub(options.center).normalize();
            options.positionsPerSide.map(positionValue=> {
                const position = (typeof positionValue === 'string')
                    // percent value
                    ? (Number.parseFloat(positionValue.rtrim('% ')) || 0) / 100
                    // absolute value
                    : positionValue / halfLineLength;
                const point = halfLine.clone().multiplyScalar(position).add(middle);
                iteratee({point, middle, edges, sideNormal, sideIndex, positionValue, options});
            });
        });
    }

    /**
     * Benutzt Scenes.iteratePointsOnSideOfRegularConvexPolygon, um eine Objekt-Factory pro Polygonpunkt aufzurufen.
     * Gibt das Array aller erzeugten Objekte zurück. Damit kann man z.B. um ein Haus herum Fenster erzeugen.
     */
    static createObjectsAroundObject(object, options, objectFactory) {
        const sphere = Scenes.boundingSphere(object);
        options = Commons.defaultsDeepSafe({}, options, {
            center: sphere.position,
            radius: sphere.radius,
            innerRadius: null,
            offset: 0,
            raycastOnModel: false,
            quaternion: Maths3.quaternion.zero,
        });


        options.radius = options.innerRadius==null
            ? options.radius
            : options.innerRadius / Math.sin((0.5-1/options.numberOfSides)*Math.PI);

        if (options.radius === 0) {
            throw "radius may not be 0";
        }
        const result = [];
        Scenes.iteratePointsOnSideOfRegularConvexPolygon(options, (p)=> {
            const raycaster = new THREE.Raycaster();
            raycaster.set(p.point.clone().add(p.sideNormal.clone().multiplyScalar(10)), p.sideNormal.clone().multiplyScalar(-1));
            const intersection = options.raycastOnModel
                ? _.head(raycaster.intersectObject(object, true))
                : null;

            const normal = intersection ? intersection.face.normal.clone() : p.sideNormal;
            const point = intersection ? intersection.point : p.point;

            point.add(normal.clone().multiplyScalar(options.offset));

            const child = objectFactory(p);
            child.position.copy(point).sub(child.parent.getWorldPosition(v3()));
            child.quaternion.setFromUnitVectors(Maths3.vector.z, normal).multiply(options.quaternion);
            result.push(child);

            if (!child.sideIndex) {
                child.sideIndex = p.sideIndex;
            }
        });
        return result;
    }

    /**
     * Manchmal werden Meshs mit Faces mit 0 Vertices geliefert. Diese Methode bereinigt das Object um diese Faces.
     * prevents warnings: [.CommandBufferContext]RENDER WARNING: Render count or primcount is 0.
     */
    static removeZeroFaceOrVerticesChildren(mesh) {

        function removedCauseEmpty(mesh, index) {
            if (mesh instanceof THREE.Mesh &&
                mesh.children.length === 0 &&
                (
                    mesh.geometry instanceof THREE.Geometry && (mesh.geometry.faces.length === 0 || mesh.geometry.vertices.length === 0 )
                    ||
                    mesh.geometry instanceof THREE.BufferGeometry && (mesh.geometry.getAttribute('position').count === 0 )
                )
            ) {
                mesh.parent.children.splice(index, 1);
                //console.log(mesh.name + " has zero faces and/or vertices and no children and so it is removed");
                return true;
            }
            return false;
        }

        function traverse(mesh) {
            for (let i = 0; i < mesh.children.length; i++) {
                const child = mesh.children[i];
                if (removedCauseEmpty(child, i)) {
                    i--;
                } else {
                    traverse(child);
                }
            }
        }

        traverse(mesh);

        return mesh;
    }

    /**
     * Klont ein Objekt und seine Materialien
     */
    static cloneWithMaterials(object) {
        let ob = object.clone(true);
        ob.traverse((o)=> {
            if (!o.material) return;
            if (o.material.type == 'MultiMaterial') {
                o.material.materials.forEach((m)=>m = Scenes.deepCloneMaterial(m));
            }
            else {
                o.material = Scenes.deepCloneMaterial(o.material);
            }
        });
        return ob;
    }

    /** */
    static deepCloneMaterial(material) {
        let m = material.clone(true)
        m.map = m.map.clone(true);
        return m;
    }

    /**
     */
    static setShadowMapSize(light,resolution) {
        const maxsize = Math.min(resolution,STAGE.renderer.capabilities.maxTextureSize);
        light.shadow.mapSize.set(maxsize, maxsize);
        if (light.shadow.map) {
            light.shadow.map.dispose();
            light.shadow.map = null;
        }
    }

    /**
     * Hiermit kann ein Licht optimal für den Schattenwurf innerhalb einer Box eingestellt werden
     */
    static setDirectionalLightShadowToCoverBoundingBox(light,box) {
        const direction = light.target.position.clone().sub(light.position).normalize();
        const radius = box.getBoundingSphere().radius;
        const zero = box.getCenter().setZ(0);
        light.position.copy(zero).add(direction.clone().multiplyScalar(-radius));
        light.target.position.copy(zero);
        const shadowCameraPlane = new THREE.Plane().setFromNormalAndCoplanarPoint(direction,light.position);
        const distances = box.getCorners().map(p=>shadowCameraPlane.distanceToPoint(p));
        const camera = light.shadow.camera;
        camera.near = _.min(distances);
        camera.far = _.max(distances);
        camera.right = radius;
        camera.left = -radius;
        camera.top = radius;
        camera.bottom = -radius;
        camera.updateProjectionMatrix();
    }

    static axisNames=['x','y','z'];

}
window['Scenes'] = Scenes;


